import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Typography } from 'antd';
import { BREAKPOINTS, Container, Flex } from '@/ui';
import userProfileBg from '@/assets/user/tabs-bg-profile.png';
import documentsBg from '@/assets/user/documents-bg.png';
import questionnairesBg from '@/assets/user/questionnaires-bg.png';
import referralsBg from '@/assets/user/referrals-bg.png';
import productsBg from '@/assets/user/products-bg.png';
import { LanguageInvestor } from '@/components/common';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CloseOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router';
import { API_URL, TABS_LABELS } from '@/utils/static';
import { useQuery } from '@tanstack/react-query';
import { kycApi } from '@/api';
import { ENABLED_STATUSES_INVESTOR } from '@/utils/static/kyc';
import { useGetStatics } from '@/hooks';

const Tab = ({ tab, isCurrent, onClick, children }) => {
  return (
    <StyledRow
      width={'calc(100% - 50px)'}
      gap={12}
      align={'center'}
      style={{ padding: '24px 0 24px 16px' }}
      onClick={onClick}
      $active={isCurrent}
      $tab={tab}
    >
      {children}
    </StyledRow>
  );
};

export const TabsMenu = ({ config, menuVisible, closeMenu, referralView }) => {
  const investor = useSelector((state) => state.investor);
  let location = useLocation();
  const navigate = useNavigate();
  const { languageId } = investor;
  const { data: statics, isLoading: isLoadingStatics } = useGetStatics();
  const { t } = useTranslation();
  const { data: questionnairesData } = useQuery({
    queryKey: ['getQuestionnaires'],
    queryFn: kycApi.getQuestionnaires,
    enabled: !referralView
  });
  const { data: productsData } = useQuery({
    queryKey: ['getMyProducts'],
    queryFn: kycApi.getMyProducts,
    enabled: !referralView
  });
  const { data: requestedDocs } = useQuery({
    queryKey: ['getRequestedDocuments'],
    queryFn: kycApi.getRequestedDocuments,
    enabled: !referralView
  });

  const activeQuestsCount = useMemo(() => {
    if (questionnairesData && questionnairesData.data) {
      return questionnairesData.data.reduce(
        (result, item) => result + (ENABLED_STATUSES_INVESTOR[item.status] ? 1 : 0),
        0
      );
    }
    return null;
  }, [questionnairesData]);

  const activeProductsCount = useMemo(() => {
    if (productsData && productsData.data) {
      return productsData.data.reduce(
        (result, item) => result + (ENABLED_STATUSES_INVESTOR[item.status] ? 1 : 0),
        0
      );
    }
    return null;
  }, [productsData]);

  const documentsCount = useMemo(() => {
    if (requestedDocs && requestedDocs.data) {
      return requestedDocs.data.reduce(
        (result, item) => result + (ENABLED_STATUSES_INVESTOR[item.status] ? 1 : 0),
        0
      );
    }
    return null;
  }, [requestedDocs]);

  const currentTabKey = useMemo(() => {
    if (!location || !config) return null;

    const path = location.pathname.split('/').pop();
    const tab = config.find((i) => i.href.includes(path === 'profile' ? '' : path));

    return tab ? tab.key : 0;
  }, [location, config]);

  const logoStatics = useMemo(
    () => statics && statics.find((item) => item.title === 'logo'),
    [statics]
  );

  const counters = {
    Questionnaires: activeQuestsCount,
    Products: activeProductsCount,
    Documents: documentsCount
  };

  return (
    <StyledContainer $width={284} $menuVisible={menuVisible}>
      <Flex vertical $height={'100%'} $paddingBottom={60} justify={'space-between'}>
        <BG $tab={currentTabKey}>
          <Container $paddingBottom={40}>
            <Container $hidden $mdVisible $padding={20}>
              <Flex justify={'flex-end'}>
                <CloseOutlined style={{ fontSize: 24 }} onClick={() => closeMenu()} />
              </Flex>
            </Container>
            <Img
              src={isLoadingStatics || !logoStatics ? '' : `${API_URL}/${logoStatics.text}`}
              //src={logoImg}
              alt='logo'
            />

            {config?.map((item) => {
              const count = counters[item.title];
              return (
                <Tab
                  isCurrent={currentTabKey === item.key}
                  onClick={() => {
                    const label = TABS_LABELS.find((tab) => location.pathname.includes(tab));
                    const pathname = location.pathname.replace(/\/$/, '');
                    navigate(
                      label ? pathname.replace(label, item.href) : pathname + '/' + item.href
                    );
                    closeMenu();
                  }}
                  tab={item.key}
                  key={item.key}
                >
                  {item.icon}

                  <Typography.Text style={{ fontSize: 15 }}>{t(item.title)}</Typography.Text>

                  {!referralView ? <>{count ? <CountTab>{count}</CountTab> : null}</> : null}
                </Tab>
              );
            })}
          </Container>
        </BG>

        <Container>
          <StyledRow align={'center'} width={'fit-content'} gap={12} $paddingLeft={16}>
            <LanguageInvestor languageId={languageId} />
          </StyledRow>
        </Container>
      </Flex>
    </StyledContainer>
  );
};

const StyledContainer = styled(Container)`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 30;
  background-color: #f6f9ff;
  height: 100vh;
  @media (max-width: ${BREAKPOINTS.md}px) {
    /* left: -500px; */
    ${({ $menuVisible }) => (!$menuVisible ? 'left: -500px;' : 'left: 0;')}
    transition: left 0.3s ease-in-out;
  }
`;

const BG = styled.div`
  background-position: top left;
  background-repeat: no-repeat;
  background: ${({ $tab }) =>
    $tab === 0
      ? `#fff url(${userProfileBg}) no-repeat`
      : $tab === 1
        ? `#fff url(${questionnairesBg}) no-repeat`
        : $tab === 2
          ? `#fff url(${documentsBg}) no-repeat`
          : $tab === 3
            ? `#fff url(${referralsBg}) no-repeat`
            : $tab === 4
              ? `#fff url(${productsBg}) no-repeat`
              : ''};
  @media (max-width: ${BREAKPOINTS.md}px) {
    background: unset;
  }
`;

const Img = styled.img`
  padding-left: 40px;
  padding-right: 40px;
  margin: 30px 0 40px;
  box-sizing: border-box;
  height: 111px;
  width: 100%;

  @media (max-width: ${BREAKPOINTS.smd}px) {
    margin: unset;
    height: 110px;
  }
`;

const StyledRow = styled(Flex)`
  margin-left: 40px;
  &:hover {
    cursor: pointer;
    svg,
    g,
    path {
      stroke: #006fff;
    }
    span {
      color: #006fff;
    }
  }

  @media (max-width: ${BREAKPOINTS.smd}px) {
    margin-left: 10px;
  }

  ${({ $active }) =>
    $active
      ? `
    pointer-events: none;
    svg,
    g,
    path {
      font-weight: 700;
      stroke: #006fff;
      stroke-width: 2px;
    }
    span {
      font-weight: 600;
      color: #006fff;
    }
  `
      : ''}

  ${({ $tab, $active }) =>
    $active && $tab === 0
      ? `
      svg,
      g,
      path {
        fill: #006fff;
        stroke: initial;
      }

      &:hover {
        svg,
        g,
        path {
          stroke: initial;
          fill: #006fff;
          stroke-width: 2px;
        }
      } 
  `
      : $tab === 0
        ? `
      &:hover {
        svg,
        g,
        path {
          stroke: initial;
          fill: #006fff;
        }
      } 
  `
        : ''}
`;

const CountTab = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background: #dde4e9;
  border-radius: 50%;
  font-size: 10px;
  color: #808089;
`;
